import { Box } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createAgentSession, exportReportPptx, getAgentSession, updateAgentSession } from 'api/agent';
import { compact } from 'es-toolkit';
import { findIndex, isEmpty, map } from 'es-toolkit/compat';
import {
  AgentSession,
  CreateAgentSessionRequest,
  ReportSection,
  createEmptySessionData,
} from 'interfaces/agentSession';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { JobResultSlideLabelModeProvider } from 'utils/jobResultAugmentation';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useCurrentUser } from 'utils/useCurrentUser';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import useSessionTransitionLoading from 'utils/useSessionTransitionLoading';

import Loader, { LoadingStage } from 'components/Loader';
import StudyReportDatasetsPanel from './StudyReportDatasetsPanel';
import { StudyReportRouteState } from './studyReportRouteNavigation';
import StudyReportSectionCard from './StudyReportSectionCard';
import {
  removeReportSectionWithTightOrder,
  reorderReportSectionsWithTightOrder,
  sortReportSections,
} from './studyReportSectionOrder';
import StudyReportSectionsLayout, {
  StudyReportLayoutSection,
  StudyReportScrollNavAttachmentInsets,
} from './StudyReportSectionsLayout';
import StudyReportWorkspaceEmptyState from './StudyReportWorkspaceEmptyState';
import StudyReportWorkspaceHeaderCard from './StudyReportWorkspaceHeaderCard';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  selectedStudyId?: string;
  selectedStudyIds?: string[];
  hasAvailableStudies?: boolean;
  onCreateStudy?: () => void;
  scrollNavAttachmentInsets?: StudyReportScrollNavAttachmentInsets;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const buildSearchWithSessionId = (search: string, sessionId: string) => {
  const searchParams = new URLSearchParams(search);
  searchParams.set('sessionId', sessionId);
  const serialized = searchParams.toString();
  return serialized ? `?${serialized}` : '';
};

const getSessionDatasetsQueryKey = (sessionId: string) => ['sessionDatasets', sessionId] as const;

// ---------------------------------------------------------------------------
// StudyReportWorkspace
// ---------------------------------------------------------------------------

interface SessionMutationContext {
  previousSession?: AgentSession;
}

// Orchestration container for Study Report workspace.
// Owns session lifecycle, persistence mutations, and top-level layout composition.
const StudyReportWorkspace: React.FC<Props> = ({
  selectedStudyId,
  selectedStudyIds = [],
  hasAvailableStudies = true,
  onCreateStudy,
  scrollNavAttachmentInsets,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const { labId } = useCurrentLabId();
  const routeState = (location.state as StudyReportRouteState | null) || null;
  const sessionId = React.useMemo(() => new URLSearchParams(location.search).get('sessionId'), [location.search]);

  // Normalize selection so all downstream code can treat study scope as a non-null array.
  const normalizedStudyIds = React.useMemo(
    () => compact(!isEmpty(selectedStudyIds) ? selectedStudyIds : selectedStudyId ? [selectedStudyId] : []),
    [selectedStudyId, selectedStudyIds]
  );
  const hasStudies = !isEmpty(normalizedStudyIds);
  const sessionQueryKey = React.useMemo(() => ['agentSession', sessionId], [sessionId]);
  const sessionCreateRequestedRef = React.useRef(false);
  const exportPptxPendingRef = React.useRef(false);

  // --- cache helpers -------------------------------------------------------

  const setCachedSession = React.useCallback(
    (session: AgentSession) => queryClient.setQueryData(['agentSession', session.id], session),
    [queryClient]
  );

  const applyMutation = React.useCallback((session: AgentSession) => setCachedSession(session), [setCachedSession]);

  const replaceSessionSections = React.useCallback(
    (session: AgentSession, sections: ReportSection[]): AgentSession => ({
      ...session,
      sessionData: {
        ...session.sessionData,
        reportState: {
          ...session.sessionData.reportState,
          sections,
        },
      },
    }),
    []
  );

  const persistSessionRecord = React.useCallback(
    (session: AgentSession) =>
      updateAgentSession(session.id, {
        title: session.title,
        studyIds: session.studyIds,
        sessionData: session.sessionData,
      }),
    []
  );

  // --- queries & mutations -------------------------------------------------

  const sessionQuery = useQuery({
    queryKey: sessionQueryKey,
    queryFn: () => getAgentSession(sessionId as string),
    enabled: Boolean(sessionId),
    staleTime: 10_000,
  });

  const isSessionTransitionLoading = useSessionTransitionLoading({
    sessionId,
    resolvedSessionId: sessionQuery.data?.id ?? null,
    isFetching: Boolean(sessionQuery.isLoading || sessionQuery.isFetching),
    isError: sessionQuery.isError,
    enabled: Boolean(sessionId),
  });

  const getCurrentSessionOrThrow = React.useCallback(() => {
    // Prefer cached data first so mutations can run against optimistic state.
    const currentSession = queryClient.getQueryData<AgentSession>(sessionQueryKey) || sessionQuery.data;
    if (!currentSession) {
      throw new Error('Report workspace session is not loaded');
    }

    return currentSession;
  }, [queryClient, sessionQuery.data, sessionQueryKey]);

  const createSessionMutation = useMutationWithErrorSnackbar({
    mutationDescription: 'Create report workspace session',
    mutationFn: (request: CreateAgentSessionRequest) => createAgentSession(request),
    onSuccess: (created) => {
      setCachedSession(created);
      queryClient.setQueryData(getSessionDatasetsQueryKey(created.id), []);
      navigate(
        { pathname: location.pathname, search: buildSearchWithSessionId(location.search, created.id) },
        { replace: true, state: location.state }
      );
    },
  });

  const updateSectionMutation = useMutationWithErrorSnackbar({
    mutationDescription: 'Update report section',
    mutationFn: ({
      sectionId: sid,
      patch,
    }: {
      sectionId: string;
      patch: { title?: string; content?: string | null };
    }) => {
      const currentSession = getCurrentSessionOrThrow();
      const nextSections = map(currentSession.sessionData.reportState.sections, (section) => {
        if (section.id !== sid) {
          return section;
        }

        return {
          ...section,
          ...(patch.title !== undefined ? { title: patch.title } : {}),
          ...(patch.content !== undefined ? { content: patch.content } : {}),
        };
      });

      return persistSessionRecord(replaceSessionSections(currentSession, nextSections));
    },
    onSuccess: applyMutation,
  });

  const deleteSectionMutation = useMutationWithErrorSnackbar<AgentSession, string, SessionMutationContext>({
    mutationDescription: 'Delete report section',
    mutationFn: (sid: string) => {
      const currentSession = getCurrentSessionOrThrow();
      return persistSessionRecord(
        replaceSessionSections(
          currentSession,
          removeReportSectionWithTightOrder(currentSession.sessionData.reportState.sections, sid)
        )
      );
    },
    onMutate: async (sid: string) => {
      // Optimistically remove the section for immediate UX feedback.
      await queryClient.cancelQueries({ queryKey: sessionQueryKey });

      const previousSession = queryClient.getQueryData<AgentSession>(sessionQueryKey);
      if (previousSession) {
        setCachedSession(
          replaceSessionSections(
            previousSession,
            removeReportSectionWithTightOrder(previousSession.sessionData.reportState.sections, sid)
          )
        );
      }

      return { previousSession };
    },
    onError: (_error, _sid, context) => {
      if (context?.previousSession) {
        setCachedSession(context.previousSession);
      }
    },
    onSuccess: applyMutation,
  });

  const reorderMutation = useMutationWithErrorSnackbar<AgentSession, string[], SessionMutationContext>({
    mutationDescription: 'Reorder report sections',
    mutationFn: (orderedSectionIds: string[]) => {
      const currentSession = getCurrentSessionOrThrow();
      return persistSessionRecord(
        replaceSessionSections(
          currentSession,
          reorderReportSectionsWithTightOrder(currentSession.sessionData.reportState.sections, orderedSectionIds)
        )
      );
    },
    onMutate: async (orderedSectionIds: string[]) => {
      // Optimistically reorder cards before server confirmation.
      await queryClient.cancelQueries({ queryKey: sessionQueryKey });

      const previousSession = queryClient.getQueryData<AgentSession>(sessionQueryKey);
      if (previousSession) {
        setCachedSession(
          replaceSessionSections(
            previousSession,
            reorderReportSectionsWithTightOrder(previousSession.sessionData.reportState.sections, orderedSectionIds)
          )
        );
      }

      return { previousSession };
    },
    onError: (_error, _orderedSectionIds, context) => {
      if (context?.previousSession) {
        setCachedSession(context.previousSession);
      }
    },
    onSuccess: applyMutation,
  });

  const removeChartMutation = useMutationWithErrorSnackbar<AgentSession, { sectionId: string; chartId: string }>({
    mutationDescription: 'Remove chart from section',
    mutationFn: ({ sectionId: sid, chartId }: { sectionId: string; chartId: string }) => {
      const currentSession = getCurrentSessionOrThrow();
      const nextSections = currentSession.sessionData.reportState.sections.map((section) =>
        section.id === sid ? { ...section, charts: section.charts.filter((chart) => chart.id !== chartId) } : section
      );

      return persistSessionRecord(replaceSessionSections(currentSession, nextSections));
    },
    onSuccess: applyMutation,
  });

  const removeTableMutation = useMutationWithErrorSnackbar<AgentSession, { sectionId: string; tableId: string }>({
    mutationDescription: 'Remove table from section',
    mutationFn: ({ sectionId: sid, tableId }: { sectionId: string; tableId: string }) => {
      const currentSession = getCurrentSessionOrThrow();
      const nextSections = currentSession.sessionData.reportState.sections.map((section) =>
        section.id === sid ? { ...section, tables: section.tables.filter((table) => table.id !== tableId) } : section
      );

      return persistSessionRecord(replaceSessionSections(currentSession, nextSections));
    },
    onSuccess: applyMutation,
  });

  const exportPptxMutation = useMutationWithErrorSnackbar<void, { sessionId: string; reportTitle: string }>({
    mutationDescription: 'Export report to PowerPoint',
    mutationFn: ({ sessionId: exportSessionId, reportTitle }: { sessionId: string; reportTitle: string }) =>
      exportReportPptx(exportSessionId, reportTitle),
  });

  // --- auto-create session on mount ----------------------------------------

  React.useEffect(() => {
    if (sessionId || !hasStudies || sessionCreateRequestedRef.current) return;
    sessionCreateRequestedRef.current = true;
    createSessionMutation.mutate({
      studyIds: normalizedStudyIds,
      title: normalizedStudyIds.length > 1 ? 'Combined analysis report' : 'Analysis report',
      sessionData: createEmptySessionData({
        activeStudyId: selectedStudyId || normalizedStudyIds[0] || null,
        labId,
        openedFrom: routeState?.previousLocation || { pathname: location.pathname, search: location.search },
        userId: currentUser.userId,
      }),
    });
  }, [
    createSessionMutation,
    currentUser.userId,
    hasStudies,
    labId,
    location.pathname,
    location.search,
    normalizedStudyIds,
    routeState,
    selectedStudyId,
    sessionId,
  ]);

  const sessionRecord = isSessionTransitionLoading ? undefined : sessionQuery.data;
  // --- derived state -------------------------------------------------------

  const orderedSections = React.useMemo(
    () => sortReportSections(sessionRecord?.sessionData.reportState.sections || []),
    [sessionRecord]
  );

  const handleMoveSection = React.useCallback(
    (sectionId_: string, direction: -1 | 1) => {
      const idx = findIndex(orderedSections, (s) => s.id === sectionId_);
      const target = idx + direction;
      if (idx < 0 || target < 0 || target >= orderedSections.length) return;

      const next = [...orderedSections];
      const [moved] = next.splice(idx, 1);
      next.splice(target, 0, moved);
      reorderMutation.mutate(map(next, (s) => s.id));
    },
    [orderedSections, reorderMutation]
  );

  const handleExportPptx = React.useCallback(() => {
    if (!sessionRecord || exportPptxPendingRef.current) {
      return;
    }

    exportPptxPendingRef.current = true;

    void exportPptxMutation
      .mutateAsync({
        sessionId: sessionRecord.id,
        reportTitle: sessionRecord.title || 'Analysis Report',
      })
      .catch(() => undefined)
      .finally(() => {
        exportPptxPendingRef.current = false;
      });
  }, [exportPptxMutation, sessionRecord]);

  // --- suggest prompt handler -----------------------------------------------

  const handleSuggestPrompt = React.useCallback(
    (prompt: string) => {
      // Route-state handoff allows the Copilot drawer to auto-seed and submit this prompt.
      navigate(location.pathname + location.search, {
        replace: true,
        state: { ...routeState, draftConversation: { initialUserPrompt: prompt, autoSubmit: true } },
      });
    },
    [navigate, location.pathname, location.search, routeState]
  );

  // --- render: guards ------------------------------------------------------

  if (!hasStudies && !sessionId) {
    return (
      <StudyReportWorkspaceEmptyState
        selectedStudyId={selectedStudyId}
        selectedStudyIds={selectedStudyIds}
        hasAvailableStudies={hasAvailableStudies}
        onCreateStudy={onCreateStudy}
      />
    );
  }

  if ((sessionId && isSessionTransitionLoading) || createSessionMutation.isLoading) {
    return (
      <Box sx={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        <Loader stage={LoadingStage.LoadingReport} />
      </Box>
    );
  }

  if (!sessionRecord) {
    return (
      <StudyReportWorkspaceEmptyState
        selectedStudyId={selectedStudyId}
        selectedStudyIds={selectedStudyIds}
        hasAvailableStudies={hasAvailableStudies}
        onCreateStudy={onCreateStudy}
      />
    );
  }

  // --- render: workspace ---------------------------------------------------

  const reportTitle = sessionRecord.title || 'Analysis report';
  const workspaceHeader = (
    <StudyReportWorkspaceHeaderCard exportIsLoading={exportPptxMutation.isLoading} onExport={handleExportPptx} />
  );
  const workspaceTopContent = (
    <StudyReportDatasetsPanel sessionId={sessionRecord.id} scopedStudyIds={normalizedStudyIds} />
  );
  const reportSections: StudyReportLayoutSection[] = orderedSections.map((section, index) => ({
    id: section.id,
    title: section.title,
    content: (
      <StudyReportSectionCard
        section={section}
        studyId={selectedStudyId || normalizedStudyIds[0]}
        sectionNumber={index + 1}
        isFirst={index === 0}
        isLast={index === orderedSections.length - 1}
        onTitleSave={(title) => updateSectionMutation.mutate({ sectionId: section.id, patch: { title } })}
        onContentSave={(content) => updateSectionMutation.mutate({ sectionId: section.id, patch: { content } })}
        onMoveUp={() => handleMoveSection(section.id, -1)}
        onMoveDown={() => handleMoveSection(section.id, 1)}
        onDelete={() => deleteSectionMutation.mutate(section.id)}
        onRemoveChart={(chartId) => removeChartMutation.mutate({ sectionId: section.id, chartId })}
        onRemoveTable={(tableId) => removeTableMutation.mutate({ sectionId: section.id, tableId })}
      />
    ),
  }));

  return (
    <JobResultSlideLabelModeProvider>
      <StudyReportSectionsLayout
        reportTitle={reportTitle}
        scrollSpyAnchor={workspaceHeader}
        topContent={workspaceTopContent}
        scrollNavAttachmentInsets={scrollNavAttachmentInsets}
        sections={reportSections}
        emptyState={
          <StudyReportWorkspaceEmptyState
            selectedStudyId={selectedStudyId}
            selectedStudyIds={selectedStudyIds}
            hasAvailableStudies={hasAvailableStudies}
            onCreateStudy={onCreateStudy}
            onSuggestPrompt={handleSuggestPrompt}
          />
        }
        sectionsContainerSx={{ pr: 1, pb: 2, minWidth: 0 }}
      />
    </JobResultSlideLabelModeProvider>
  );
};

export default StudyReportWorkspace;
